// src/pages/Integrations/IntegrationAccounts.tsx
import React, { useEffect, useState } from "react";
import {
    Card,
    CardHeader,
    CardBody,
    Nav,
    NavItem,
    NavLink,
    TabContent,
    TabPane,
    ListGroup,
    ListGroupItem,
    Button,
    Alert,
} from "reactstrap";
import classnames from "classnames";
import { useNavigate } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import { ThunkDispatch, Action } from "@reduxjs/toolkit";

import ConfirmConnectModal from "../Actions/ConfirmConnectModal";
import ApiKeyModal from "../Actions/ApiKeyModal";
import DnsConnectModal from "../Actions/DnsConnectModal";
import DeleteAccountModal from "../Actions/DeleteAccountModal";

import {
    installRequest,
    oauthInitiateRequest,
    uninstallRequest,
    mappingGetByAppRequest,
    mappingDeleteRequest,
    getAppRequest
} from "store/integrations/action";
import type {
    AppDTO,
    AppVersionDTO,
    InstallationSummaryDTO,
    MappingRecord,
} from "store/integrations/types";
import type { ApplicationState } from "store";
import DeleteMappingModal from "../Actions/DeleteMappingModal";

interface Props {
    tenantId: string;
    app: AppDTO;
    docsUrl: string;
}

const IntegrationAccounts: React.FC<Props> = ({ tenantId, app, docsUrl }) => {
    const dispatch = useDispatch<ThunkDispatch<any, any, Action>>();
    const navigate = useNavigate();

    // base for your setup URLs (guard against missing metadata)
    const setupBase = app.version_metadata?.ui?.setupUrl ?? "/integrations/setup";
    const requiresSetup = app.version_metadata?.ui?.requiresSetup;

    const installs = app.installations || [];
    const connected = installs.filter((i) => i.status === "ACTIVE");
    const pending = installs.filter((i) => i.status === "PENDING_SETUP");

    const [tab, setTab] = useState<"connected" | "pending">("connected");
    const [confirmApp, setConfirmApp] = useState<AppDTO | null>(null);
    const [apiKeyApp, setApiKeyApp] = useState<AppDTO | null>(null);
    const [deleteInst, setDeleteInst] = useState<InstallationSummaryDTO | null>(null);
    const [mappingToDelete, setMappingToDelete] = useState<{
        installId: string;
        rec: MappingRecord;
    } | null>(null);
    const [oauthLoading, setOauthLoading] = useState(false);
    const [dnsModal, setDnsModal] = useState<AppDTO | null>(null);


    const mappingRecords = useSelector(
        (s: ApplicationState) => s.integration.mappingRecords
    );
    const loadingMappings = useSelector(
        (s: ApplicationState) => s.integration.loadingMappingRecords
    );

    // reload mappings when we switch to “connected”
    useEffect(() => {
        if (tab === "connected") {
            dispatch(mappingGetByAppRequest(tenantId, app.id));
        }
    }, [tab, tenantId, app.id, dispatch]);

    const onConnect = () => {
        const authType = app.version_metadata?.authType || "none";
        if (authType === "oauth") {
          setConfirmApp(app);
        } else if (authType === "apikey") {
          setApiKeyApp(app);
        } else if (authType === "dns") {
          setDnsModal(app);
        } else {
          dispatch(
            installRequest(
              tenantId,
              app.latest_version_id,
              "{}",
              () => dispatch(getAppRequest(tenantId, app.id)),
              console.error
            )
          );
        }
      };
      

    const onConfirmOauth = () => {
        if (!confirmApp) return;
        setOauthLoading(true);
        dispatch(
            oauthInitiateRequest(
                tenantId,
                confirmApp.id,
                (url: string) => (window.location.href = url),
                (err: any) => {
                    console.error(err);
                    setOauthLoading(false);
                    setConfirmApp(null);
                }
            )
        );
    };
    const onSubmitApiKey = (config: Record<string, string>) => {
        if (!apiKeyApp) return;
        setApiKeyApp(null);
        dispatch(
            installRequest(
                tenantId,
                apiKeyApp.latest_version_id,
                config?.name,
                JSON.stringify(config),
                () => dispatch(mappingGetByAppRequest(tenantId, app.id)),
                console.error
            )
        );
    };

    const onDisconnect = (inst: InstallationSummaryDTO) => setDeleteInst(inst);
    const onConfirmDisconnect = () => {
        if (!deleteInst) return;
        dispatch(
            uninstallRequest(
                tenantId,
                deleteInst.id,
                () => {
                    setDeleteInst(null);
                    dispatch(getAppRequest(tenantId, app.id));
                    dispatch(mappingGetByAppRequest(tenantId, app.id));
                },
                console.error
            )
        );
    };

    const editSetup = (instId: string, recId?: string) => {
        const url = recId
            ? `${setupBase}/${instId}/${recId}`
            : `${setupBase}/${instId}`;
        navigate(url);
    };

    // how many shimmer rows to show
    const shimmerCount = 4;

    return (
        <>
            {/* Modals */}
            {confirmApp && (
                <ConfirmConnectModal
                    show
                    onCloseClick={() => setConfirmApp(null)}
                    selectedPlatform={{
                        app: { name: confirmApp.name, logo_url: confirmApp.logo_url },
                        config: {},
                    }}
                    onConfirm={onConfirmOauth}
                    loading={oauthLoading}
                />
            )}
            {apiKeyApp && (() => {
                const credentials = app.version_metadata?.credentials as any[] | undefined;
                if (!credentials?.length) return null;

                return (
                    <ApiKeyModal
                        show
                        appName={apiKeyApp.name}
                        installName={`New ${apiKeyApp.name} Account`}
                        credentials={credentials}
                        onClose={() => setApiKeyApp(null)}
                        onSubmit={onSubmitApiKey}
                    />
                );
            })()}
            {dnsModal && (
            <DnsConnectModal
                show={!!dnsModal}
                app={dnsModal}
                fields={app.version_metadata?.authFlow?.fields || []}
                operations={app.version_metadata?.authFlow?.operations || {}}
                installRequires={app.version_metadata?.authFlow?.installRequires || []}
                tenantId={tenantId}
                onClose={() => setDnsModal(null)}
                onInstall={async (domain: string) => {
                await dispatch(
                    installRequest(
                    tenantId,
                    app.latest_version_id,
                    domain,
                    JSON.stringify({ domain }),
                    () => {
                        setDnsModal(null);
                        dispatch(getAppRequest(tenantId, app.id));
                    },
                    (err: { message: string }) => alert("Failed: " + err?.message)
                    )
                );
                }}
            />
            )}

            {deleteInst && (
                <DeleteAccountModal
                    show
                    record={deleteInst}
                    onClose={() => setDeleteInst(null)}
                    onConfirm={onConfirmDisconnect}
                />
            )}
            {mappingToDelete && (
                <DeleteMappingModal
                    show
                    record={mappingToDelete.rec}
                    onClose={() => setMappingToDelete(null)}
                    onConfirm={() => {
                        dispatch(
                            mappingDeleteRequest(
                                tenantId,
                                mappingToDelete.installId,
                                mappingToDelete.rec.id
                            )
                        );
                        setMappingToDelete(null);
                    }}
                />
            )}

            <Card className="shadow-sm mb-5">
                <CardHeader className="d-flex align-items-center">
                    <Nav pills>
                        <NavItem>
                            <NavLink
                                className={classnames({ active: tab === "connected" })}
                                onClick={() => setTab("connected")}
                            >
                                Connected ({connected.length})
                            </NavLink>
                        </NavItem>
                        <NavItem>
                            <NavLink
                                className={classnames({ active: tab === "pending" })}
                                onClick={() => setTab("pending")}
                            >
                                Pending ({pending.length})
                            </NavLink>
                        </NavItem>
                    </Nav>
                    <Button color="success" size="sm" className="ms-auto" onClick={onConnect}>
                        <i className="ri-add-line" /> Add Account
                    </Button>
                </CardHeader>

                <CardBody className="p-0">
                    <TabContent activeTab={tab}>
                        {/* Connected Tab */}
                        <TabPane tabId="connected">
                            {loadingMappings ? (
                                <ListGroup flush>
                                    {Array.from({ length: shimmerCount }).map((_, i) => (
                                        <ListGroupItem
                                            key={i}
                                            className="d-flex justify-content-between align-items-center py-3"
                                        >
                                            <div className="w-50 placeholder-glow">
                                                <span className="placeholder col-5"></span>
                                            </div>
                                            <div className="placeholder-glow text-end">
                                                <span className="placeholder col-2"></span>
                                                <span className="placeholder col-1 ms-1"></span>
                                            </div>
                                        </ListGroupItem>
                                    ))}
                                </ListGroup>
                            ) : connected.length ? (
                                connected.map((inst) => {
                                    const recs = mappingRecords.filter(
                                        (r) => r.installation_id === inst.id
                                    );
                                    return (
                                        <Card key={inst.id} className="m-0 pb-2">
                                            <CardHeader className="d-flex justify-content-between py-2">
                                                <strong>{inst.name}</strong>
                                                <Button
                                                    size="sm"
                                                    color="danger"
                                                    className="btn-label"
                                                    onClick={() => onDisconnect(inst)}
                                                >
                                                    <i className="ri-logout-box-line label-icon align-middle fs-16 me-2"></i>
                                                    Disconnect
                                                </Button>
                                            </CardHeader>
                                            {requiresSetup && (
                                            <CardBody className="py-2">
                                                {recs.length ? (
                                                    <ListGroup flush>
                                                        {recs.map((rec) => (
                                                            <ListGroupItem
                                                                key={rec.id}
                                                                className="d-flex justify-content-between align-items-center py-2"
                                                            >
                                                                <div>
                                                                    {rec.name}
                                                                    <br />
                                                                    <small className="text-muted">
                                                                        {rec.updated_at
                                                                            ? new Date(rec.updated_at).toLocaleString()
                                                                            : "—"}
                                                                    </small>
                                                                </div>
                                                                <div>
                                                                    <Button
                                                                        size="sm"
                                                                        color="danger"
                                                                        onClick={() =>
                                                                            setMappingToDelete({ installId: inst.id, rec })
                                                                        }
                                                                    >
                                                                        <i className="ri-delete-bin-fill" />
                                                                    </Button>
                                                                </div>
                                                            </ListGroupItem>
                                                        ))}
                                                    </ListGroup>
                                                ) : (
                                                    <Alert color="secondary" className="text-center py-2">
                                                        No setups yet
                                                    </Alert>
                                                )}
                                                <Button
                                                    size="sm"
                                                    color="primary"
                                                    onClick={() => editSetup(inst.id)}
                                                    className="mt-2"
                                                >
                                                    <i className="ri-add-line" /> New Setup
                                                </	Button>
                                            </CardBody>
                                            )}
                                        </Card>
                                    );
                                })
                            ) : (
                                <Card className="border-0 text-center m-0">
                                    <CardBody className="py-4">
                                        <i className="ri-plug-line fs-2 text-secondary mb-2" />
                                        <div>No accounts connected</div>
                                        <small className="text-muted d-block mb-2">
                                            Add an account to get started.
                                        </small>
                                        <Button color="primary" size="sm" onClick={onConnect}>
                                            Add Account
                                        </Button>
                                    </CardBody>
                                </Card>
                            )}
                        </TabPane>

                        {/* Pending Tab */}
                        <TabPane tabId="pending">
                            {pending.length ? (
                                <ListGroup flush>
                                    {pending.map((inst) => (
                                        <ListGroupItem
                                            key={inst.id}
                                            className="d-flex justify-content-between align-items-center py-2"
                                        >
                                            <div>
                                                <strong>{inst.name}</strong>
                                                <br />
                                                <small className="text-warning">
                                                    <i className="ri-error-warning-line" /> Setup Needed
                                                </small>
                                            </div>
                                            <div>
                                                <Button
                                                    size="sm"
                                                    color="warning"
                                                    onClick={() => editSetup(inst.id)}
                                                >
                                                    Continue
                                                </Button>{" "}
                                                <Button
                                                    size="sm"
                                                    color="outline-danger"
                                                    onClick={() => onDisconnect(inst)}
                                                >
                                                    <i className="ri-delete-bin-line" />
                                                </Button>
                                            </div>
                                        </ListGroupItem>
                                    ))}
                                </ListGroup>
                            ) : (
                                <Card className="border-0 text-center m-0">
                                    <CardBody className="py-4">
                                        <i className="ri-time-line fs-2 text-secondary mb-2" />
                                        <div>No pending setups</div>
                                        <small className="text-muted d-block">
                                            All installations are configured.
                                        </small>
                                    </CardBody>
                                </Card>
                            )}
                        </TabPane>
                    </TabContent>
                </CardBody>
            </Card>
        </>
    );
};

export default IntegrationAccounts;
